import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CreditCard, AlertTriangle, CheckCircle2, ArrowRight } from "lucide-react";

const SELLER_ROLES = ["organizer", "venue_owner", "provider"];
const CACHE_KEY = "em-stripe-status-cache-v1";

/**
 * Compact status banner shown on seller dashboards.
 * - Not rendered for non-seller roles.
 * - Uses a 60s localStorage cache so it doesn't hit the API on every mount.
 */
export default function StripeConnectBanner() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !SELLER_ROLES.includes(user.role)) {
      setLoading(false);
      return;
    }
    // Read cache
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
      if (cached && cached.userId === user.user_id && Date.now() - cached.ts < 60_000) {
        setStatus(cached.data);
        setLoading(false);
        return;
      }
    } catch { /* ignore */ }

    let cancelled = false;
    api.get("/stripe/connect/status")
      .then(({ data }) => {
        if (cancelled) return;
        setStatus(data);
        localStorage.setItem(CACHE_KEY, JSON.stringify({ userId: user.user_id, ts: Date.now(), data }));
      })
      .catch(() => { /* silent */ })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [user]);

  if (loading || !user || !SELLER_ROLES.includes(user.role) || !status) return null;
  if (status.ready_to_sell) return null;  // hide once fully configured

  // Not started yet
  if (!status.onboarded) {
    return (
      <div data-testid="stripe-banner-onboard" className="bg-amber-50 border border-amber-200 rounded-md p-5 mb-8">
        <div className="flex items-start gap-3 flex-wrap">
          <AlertTriangle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm font-semibold text-amber-900 mb-1">
              Activez vos paiements en ligne
            </p>
            <p className="text-xs text-amber-800 leading-relaxed">
              Pour recevoir les paiements de vos exposants/clients, configurez Stripe Connect
              (5 min, gratuit, sécurisé PCI-DSS).
            </p>
          </div>
          <Link
            to="/dashboard/stripe"
            data-testid="stripe-banner-link"
            className="inline-flex items-center gap-1.5 bg-amber-700 hover:bg-amber-800 text-white text-xs font-semibold px-4 py-2 rounded transition-colors"
          >
            <CreditCard className="h-3.5 w-3.5" /> Activer <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    );
  }

  // Onboarded but not ready (pending KYC or requirements due)
  return (
    <div data-testid="stripe-banner-pending" className="bg-blue-50 border border-blue-200 rounded-md p-5 mb-8">
      <div className="flex items-start gap-3 flex-wrap">
        <CheckCircle2 className="h-5 w-5 text-blue-700 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm font-semibold text-blue-900 mb-1">
            Onboarding Stripe en cours de validation
          </p>
          <p className="text-xs text-blue-800 leading-relaxed">
            {status.requirements_due?.length > 0
              ? `Il reste ${status.requirements_due.length} information(s) à compléter.`
              : "Stripe termine la vérification de votre compte (5-30 min habituellement)."}
          </p>
        </div>
        <Link
          to="/dashboard/stripe"
          data-testid="stripe-banner-continue"
          className="inline-flex items-center gap-1.5 bg-blue-700 hover:bg-blue-800 text-white text-xs font-semibold px-4 py-2 rounded transition-colors"
        >
          Voir le statut <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}
